import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2, Plus, CheckCircle2, Trash2 } from 'lucide-react'
import {
  useAdminCarBrands,
  useAdminCarModels,
  useAdminCarGenerations,
  useAdminCarVariants,
  useCreateVariant,
  useDeleteVariant,
} from '../../../hooks/use-admin-cars'
import { DeleteConfirmDialog } from '../components/delete-confirm-dialog'

export function VariantsPage() {
  const { data: brands } = useAdminCarBrands()
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(undefined)
  const activeBrandId = selectedBrandId ?? (brands?.[0]?.id || 0)

  const { data: models } = useAdminCarModels(activeBrandId)
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(undefined)
  const activeModelId = selectedModelId ?? (models?.[0]?.id || 0)

  const { data: generations } = useAdminCarGenerations(activeModelId)
  const [selectedGenId, setSelectedGenId] = useState<number | undefined>(undefined)
  const activeGenId = selectedGenId ?? (generations?.[0]?.id || 0)

  const { data: variants, isLoading } = useAdminCarVariants(activeGenId)
  const createVariant = useCreateVariant()
  const deleteVariant = useDeleteVariant()

  const [open, setOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [name, setName] = useState('')
  const [fuelType, setFuelType] = useState('DIESEL')
  const [transmission, setTransmission] = useState('AUTOMATIC')
  const [engineCc, setEngineCc] = useState('2755')
  const [horsepower, setHorsepower] = useState('201')
  const [torqueNm, setTorqueNm] = useState('500')
  const [mileageKmpl, setMileageKmpl] = useState('14.2')
  const [seatingCapacity, setSeatingCapacity] = useState('7')

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !activeGenId) return
    await createVariant.mutateAsync({
      generationId: activeGenId,
      name,
      fuelType,
      transmission,
      engineCc: Number(engineCc) || undefined,
      horsepower: Number(horsepower) || undefined,
      torqueNm: Number(torqueNm) || undefined,
      mileageKmpl: Number(mileageKmpl) || undefined,
      seatingCapacity: Number(seatingCapacity) || undefined,
    })
    setName('')
    setOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Car Variants Catalog</h1>
          <p className="text-sm text-muted-foreground">Manage exact engine specs &amp; trim variants (Brand → Model → Generation → Variant).</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button disabled={!activeGenId} className="gap-2">
              <Plus className="size-4" /> Add Trim Variant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Add Trim Variant under Generation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Variant Name *</label>
                <Input placeholder="e.g. Legender 2.8 4x4 AT" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fuel Type</label>
                  <select value={fuelType} onChange={(e) => setFuelType(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
                    <option value="PETROL">Petrol</option>
                    <option value="DIESEL">Diesel</option>
                    <option value="ELECTRIC">Electric</option>
                    <option value="HYBRID">Hybrid</option>
                    <option value="CNG">CNG</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Transmission</label>
                  <select value={transmission} onChange={(e) => setTransmission(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
                    <option value="MANUAL">Manual</option>
                    <option value="AUTOMATIC">Automatic</option>
                    <option value="AMT">AMT</option>
                    <option value="CVT">CVT</option>
                    <option value="DCT">DCT</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Engine (CC)</label>
                  <Input type="number" value={engineCc} onChange={(e) => setEngineCc(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Horsepower (HP)</label>
                  <Input type="number" value={horsepower} onChange={(e) => setHorsepower(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Torque (Nm)</label>
                  <Input type="number" value={torqueNm} onChange={(e) => setTorqueNm(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mileage (km/l)</label>
                  <Input type="number" step="0.1" value={mileageKmpl} onChange={(e) => setMileageKmpl(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Seating Capacity</label>
                  <Input type="number" value={seatingCapacity} onChange={(e) => setSeatingCapacity(e.target.value)} />
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createVariant.isPending}>
                  {createVariant.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Save Variant'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Cascading Selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">1. Select Brand</label>
          <select value={String(activeBrandId)} onChange={(e) => { setSelectedBrandId(Number(e.target.value)); setSelectedModelId(undefined); setSelectedGenId(undefined); }} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            {(brands || []).map((b: any) => (
              <option key={b.id} value={String(b.id)}>{b.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">2. Select Model</label>
          <select value={String(activeModelId)} onChange={(e) => { setSelectedModelId(Number(e.target.value)); setSelectedGenId(undefined); }} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            {(models || []).map((m: any) => (
              <option key={m.id} value={String(m.id)}>{m.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">3. Select Generation</label>
          <select value={String(activeGenId)} onChange={(e) => setSelectedGenId(Number(e.target.value))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            {(generations || []).map((g: any) => (
              <option key={g.id} value={String(g.id)}>{g.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Variants Table */}
      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading vehicle trim variants...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Variant Name</th>
                <th className="p-4">Fuel &amp; Trans</th>
                <th className="p-4">Engine Specs</th>
                <th className="p-4">Mileage &amp; Seats</th>
                <th className="p-4">Listings</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(variants || []).map((v: any) => {
                const isIndependent = (v._count?.listings ?? 0) === 0
                return (
                  <tr key={v.id} className="hover:bg-accent/20 transition-colors">
                    <td className="p-4 font-bold flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-emerald-500" />
                      <span>{v.name}</span>
                    </td>
                    <td className="p-4">
                      <div className="font-semibold text-xs">{v.fuelType}</div>
                      <div className="text-xs text-muted-foreground">{v.transmission}</div>
                    </td>
                    <td className="p-4 font-mono text-xs">
                      {v.engineCc ? `${v.engineCc} cc` : 'N/A'} • {v.horsepower ? `${v.horsepower} HP` : 'N/A'}
                    </td>
                    <td className="p-4 text-xs font-semibold">
                      {v.mileageKmpl ? `${v.mileageKmpl} km/l` : 'N/A'} • {v.seatingCapacity ? `${v.seatingCapacity} Seater` : 'N/A'}
                    </td>
                    <td className="p-4 font-semibold">{v._count?.listings ?? 0}</td>
                    <td className="p-4">
                      <Badge className={v.isActive ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}>
                        {v.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(v)}
                        className={`size-8 p-0 ${
                          isIndependent
                            ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-500/10'
                            : 'text-muted-foreground/50 hover:text-amber-600 hover:bg-amber-500/10'
                        }`}
                        title={
                          isIndependent
                            ? `Delete independent variant "${v.name}"`
                            : `Cannot delete: Has ${v._count?.listings || 0} listings`
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteVariant.mutateAsync({ id: deleteTarget.id, generationId: activeGenId })
          }}
          title={`Delete Variant: ${deleteTarget.name}`}
          entityName={deleteTarget.name}
          entityType="Variant"
          isIndependent={(deleteTarget._count?.listings ?? 0) === 0}
          blockingReason={`Cannot delete variant "${deleteTarget.name}" because it has ${deleteTarget._count?.listings || 0} listing(s) attached. Only independent variants can be deleted.`}
          dependencyDetails={[
            { label: 'Listings', count: deleteTarget._count?.listings ?? 0 },
          ]}
        />
      )}
    </div>
  )
}
